/**
 * 전환 API 이벤트 현황 페이지
 *
 * 이벤트 통계(날짜별 바 차트) + 이벤트 로그(컴팩트 리스트 + 사이드 패널 상세).
 * 통계에서 날짜 클릭 시 로그가 해당 날짜로 필터링됨.
 */
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  PackageX,
  RefreshCw,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  getGetMyCapiEventLogsQueryKey,
  useGetMyCapiEventDetail,
  useGetMyCapiEventLogs,
  useGetMyCapiEventStats,
} from '@/api/generated/v2-meta/v2-meta'
import type {
  MetaEventLogDetailResponse,
  MetaEventLogResponse,
  MetaEventLogsListResponse,
  MetaEventStatItem,
} from '@/api/schemas'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Pagination } from '@/components/ui/Pagination'
import { Select, SelectOption } from '@/components/ui/Select'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'
import { useQueryClient } from '@tanstack/react-query'

export const Route = createFileRoute('/_dashboard/capi')({
  component: CapiDashboardPage,
})

// ============================================
// 상수 & 매핑
// ============================================

const RANGE_DAYS = 7
const PAGE_SIZE = 30

const EVENT_KO: Record<string, string> = {
  PageView: '페이지 조회',
  ViewContent: '콘텐츠 조회',
  AddToCart: '장바구니 추가',
  InitiateCheckout: '결제 시작',
  Purchase: '구매 완료',
  Search: '검색',
  CompleteRegistration: '회원가입',
  Lead: '잠재고객',
  AddPaymentInfo: '결제정보 추가',
}
function eventKo(name: string) {
  return EVENT_KO[name] || name
}

/** 기본 chip 목록 (자주 사용되는 순서) + 데이터에서 추가로 발견된 이벤트 자동 포함 */
const BASE_EVENT_ORDER = [
  'Purchase',
  'InitiateCheckout',
  'AddToCart',
  'ViewContent',
  'CompleteRegistration',
  'AddPaymentInfo',
  'Search',
  'Lead',
  'PageView',
]

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'success', label: '성공' },
  { value: 'failed', label: '실패' },
  // 전송을 시도조차 안 한 건 (기기 시각이 어긋나 event_time 이 7일을 넘은 경우 등).
  // 성공으로 보이면 "왜 메타에 없지?" 로 이어지므로 실패와도, 성공과도 구분한다.
  { value: 'skipped', label: '전송 안 됨' },
] as const

// ============================================
// 전송 이력 배지
// ============================================
//
// 재전송 경로가 셋인데 각각 다른 필드에 흔적을 남긴다. 하나로 뭉뚱그리면 원인 추적이
// 안 되므로 배지를 따로 낸다.
//
//  1. 보강 재전송 (is_resend)    — PII 를 채워 다시 보낸 건. **새 로그 행**이 생긴다
//  2. 즉시 재시도 (retry_count)  — 전송 요청 안에서 동기로 곧장 다시 시도한 건
//  3. 자동 복구  (batch_retry_*) — 스케줄러가 나중에 **같은 행을 업데이트**한 건
//
// 3번이 성공하면 status 가 'success' 로 바뀌고 error_message 는 first_attempt_error 로
// 옮겨간다 (services/capi_batch_retry_service.py retry_event). 그래서 배지가 없으면
// 첫 시도부터 성공한 건과 화면상 완전히 같아진다 — 과거 전역 토큰 레이스 때 하루
// 1,600건이 이렇게 조용히 복구되고 있었는데 화면에는 아무 흔적도 없었다.
//
// ⚠️ 2번은 **현재 코드에서는 안 생긴다.** 전송 클라이언트가 1회만 시도하고 모든 반환
// 경로에서 retry_count 를 0 으로 박는다 (services/meta/capi/client.py:197,253,271 —
// "1회 시도 — 실패 시 batch retry job 이 매시간 재처리"). prod 실측으로도 최근 14일
// retry_count > 0 은 0건이다. 그래도 배지를 남기는 건 과거 행에는 값이 있고, 동기
// 재시도가 되살아나면 그때 조용히 묻히는 걸 막기 위해서다.
//
// 어드민 화면(features/admin/MetaCapiSection.tsx)의 같은 배지와 대응:
//   보강 재전송 = "재전송" / 즉시 재시도 = "재시도 성공" / 자동 복구 = "배치 복구"
// 어드민은 내부 용어를, 여기서는 고객이 읽을 말을 쓴다. **조건도 하나 다르다** —
// 어드민의 "재시도 성공" 은 status === 'success' && !batch_retry_status 일 때만 뜨지만
// 여기서는 retry_count > 0 이면 항상 띄운다. 라벨에 "성공"이 없어 게이트가 필요 없고,
// 이력을 빠짐없이 보여주는 쪽이 원인 추적에 맞기 때문이다. 두 화면을 나란히 놓고
// 비교할 일이 있으면 이 차이를 감안할 것.

// 배치가 앞으로 더 시도할지는 화면에서 판정하지 않는다.
//
// 백엔드는 batch_retry_count < 3 **그리고** event_time > now-3h 인 건만 집어간다
// (services/capi_batch_retry_service.py MAX_BATCH_RETRY_COUNT · EVENT_TIME_WINDOW_HOURS).
// 두 조건을 프론트에서 다시 계산해 "재전송 대기 / 재전송 실패"로 갈라 봤는데, 상수 두 개를
// 복제하는 데다 event_time 파싱이 실패하면 오히려 "영구 유실"이라는 가장 센 주장이 나왔다.
// 리뷰에서 이 축이 계속 새 결함을 만들어 걷어냈다.
//
// 지금은 **일어난 사실만** 말한다 — batch_retry_status 가 'failed' 면 "재전송 실패"다.
// 그 뒤 실제로 복구되면 status 가 'success' 로 바뀌며 배지도 "자동 복구"로 바뀌므로,
// 잠깐의 과장은 스스로 교정된다. 반대 방향(대기라고 해놓고 영영 안 오는 것)은 교정되지 않는다.
// 남은 시도 횟수를 정말 보여줘야 한다면 백엔드가 "재시도 예정" 여부를 내려주는 쪽이 맞다.

type ResendFieldKeys =
  | 'is_resend'
  | 'retry_count'
  | 'batch_retry_count'
  | 'batch_retry_status'

/**
 * 목록·상세 응답이 공통으로 갖는 재전송 필드.
 *
 * 손으로 적은 구조적 타입이 아니라 **생성 타입에서 Pick 한다.** 수제 타입이면 백엔드가
 * 필드를 빼거나 이름을 바꿔도 컴파일이 통과하고 배지만 조용히 사라진다 — 이 화면이
 * 막으려는 실패가 정확히 그것이다.
 *
 * **목록·상세 양쪽에서 Pick 해 교차**하는 게 요점이다. 목록 타입만 Pick 하면 상세 쪽
 * 드리프트를 못 잡는다 — 뽑은 필드가 전부 optional 이라, 상세 스키마에서 필드가 빠져도
 * 그 객체는 여전히 이 타입에 대입된다(겹치는 필드가 남아 weak type 검사도 안 걸린다).
 * 교차해 두면 어느 쪽에서 빠지든 그 Pick 이 컴파일 에러를 낸다.
 */
type ResendFields = Pick<MetaEventLogResponse, ResendFieldKeys> &
  Pick<MetaEventLogDetailResponse, ResendFieldKeys>

type DeliveryBadge = {
  key: string
  label: string
  variant: 'success' | 'warning' | 'danger' | 'secondary'
}

/**
 * 이벤트가 지나온 재전송 경로를 배지 목록으로 만든다.
 * 배지는 "무슨 일이 있었나"만 담고 횟수·시각·사유는 상세 패널이 맡는다 (목록 행 폭 보호).
 */
function deliveryBadges(ev: ResendFields): DeliveryBadge[] {
  const badges: DeliveryBadge[] = []

  if (ev.is_resend) {
    badges.push({ key: 'resend', label: '보강 재전송', variant: 'secondary' })
  }
  if ((ev.retry_count ?? 0) > 0) {
    badges.push({ key: 'retry', label: '즉시 재시도', variant: 'warning' })
  }

  // 배치 재전송은 결과까지 갈라 보여준다. 복구됐는지 / 아직 실패인지 / 아예 못 했는지가
  // 고객 조치(설정 확인 vs 대기)를 가른다.
  const batchCount = ev.batch_retry_count ?? 0
  if (batchCount > 0 || ev.batch_retry_status) {
    if (ev.batch_retry_status === 'success') {
      // 성공이지만 초록이 아니라 노랑이다 — 첫 시도는 실패했다는 사실이 요점이라
      // 초록으로 칠하면 다시 "그냥 잘 됨"으로 읽힌다.
      badges.push({ key: 'batch', label: '자동 복구', variant: 'warning' })
    } else if (ev.batch_retry_status === 'failed') {
      // 앞으로 더 시도할지는 판정하지 않는다 (위 주석 참고). 마지막 배치 시도가
      // 실패했다는 사실만 말한다.
      badges.push({ key: 'batch', label: '재전송 실패', variant: 'danger' })
    } else if (ev.batch_retry_status === 'skipped') {
      // 재전송 자체를 못 한 건. 사유가 둘이라(설정 누락 / 보낼 수 없는 이벤트)
      // 배지는 중립으로 두고 사유는 상세가 batch_retry_error 원문으로 보여준다.
      badges.push({ key: 'batch', label: '재전송 보류', variant: 'secondary' })
    } else {
      // status 없이 count 만 있는 조합은 정상 경로엔 없다. 그래도 흔적은 삼키지 않는다.
      badges.push({ key: 'batch', label: '재전송 시도', variant: 'warning' })
    }
  }

  return badges
}

// 배치 재전송 결과의 한글 표기 (상세 패널용).
//
// skipped 의 사유는 여기 박지 않는다 — 원천이 둘이라(설정 누락 / 보낼 수 없는 이벤트)
// 한쪽 문구를 박으면 다른 쪽에 거짓말이 된다. 문자열 접두사로 갈라 한글로 옮겨봤지만,
// 백엔드 내부 문자열에 화면 문구를 매다는 결합이 생기고 옮기지 못한 사유는 삼켜졌다.
// 지금은 사유를 batch_retry_error 원문 그대로 보여준다 — 영어라도 사라지는 것보다 낫다.
// (P0 가 원천에서 값을 가르기로 했으니, 그 값이 오면 그때 한글 문구를 붙인다.)
const BATCH_RETRY_STATUS_KO: Record<string, string> = {
  success: '성공',
  failed: '실패',
  skipped: '건너뜀',
}

// ============================================
// 유틸
// ============================================

function fmtDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function fmtShort(s: string) {
  return s.slice(5)
}

function getDefaultRange() {
  const e = new Date()
  const s = new Date()
  s.setDate(s.getDate() - 6)
  return { start: s, end: e }
}
function shiftRange(s: Date, e: Date, days: number) {
  const ns = new Date(s)
  const ne = new Date(e)
  ns.setDate(ns.getDate() + days)
  ne.setDate(ne.getDate() + days)
  return { start: ns, end: ne }
}
function getDateRange(start: Date, end: Date) {
  const dates: string[] = []
  const d = new Date(start)
  while (d <= end) {
    dates.push(fmtDate(d))
    d.setDate(d.getDate() + 1)
  }
  return dates
}

/** UTC → KST "HH:mm:ss" (24시간) */
function fmtTime(iso: string) {
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z'
  const d = new Date(s)
  const h = String(
    d.toLocaleString('en-US', {
      timeZone: 'Asia/Seoul',
      hour: '2-digit',
      hour12: false,
    }),
  ).padStart(2, '0')
  const m = String(
    d.toLocaleString('en-US', { timeZone: 'Asia/Seoul', minute: '2-digit' }),
  ).padStart(2, '0')
  const sec = String(
    d.toLocaleString('en-US', { timeZone: 'Asia/Seoul', second: '2-digit' }),
  ).padStart(2, '0')
  return `${h}:${m}:${sec}`
}

/** UTC → KST "MM-DD HH:mm:ss" */
function fmtDateTime(iso: string) {
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z'
  const d = new Date(s)
  const p = d
    .toLocaleString('en-US', {
      timeZone: 'Asia/Seoul',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    .split(', ')
  return `${p[0].replace('/', '-')} ${p[1]}`
}

/** UTC → KST "2026년 4월 4일 12:26:16" */
function fmtFull(iso: string) {
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z'
  return new Date(s).toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

// ============================================
// 메인 페이지 — 날짜 필터 상태를 통계↔로그 간 공유
// ============================================

function CapiDashboardPage() {
  const navigate = useNavigate()
  useEffect(() => {
    if (!auth.isAuthenticated()) navigate({ to: '/' })
  }, [navigate])

  // 통계에서 날짜/이벤트 클릭 → 로그 필터
  const [dateFilter, setDateFilter] = useState<string | null>(null)
  const [eventFilter, setEventFilter] = useState('')

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-xl font-bold text-on-surface">이벤트 현황</h1>
        <p className="text-sm text-on-surface-variant mt-1">
          Meta 전환 API로 전송된 이벤트를 확인할 수 있어요.
        </p>
      </div>
      <CapiStatsSection
        onDateClick={(date) => setDateFilter(date === dateFilter ? null : date)}
        selectedDate={dateFilter}
        eventFilter={eventFilter}
        onEventFilterChange={setEventFilter}
      />
      <CapiLogsSection
        dateFilter={dateFilter}
        onClearDate={() => setDateFilter(null)}
        eventFilter={eventFilter}
      />
      <ProductGroupEntryCard />
    </div>
  )
}

// ============================================
// 상품 그룹 안내 (진입점)
// ============================================

/**
 * 광고 성과에서 특정 상품 빼기 기능의 진입점.
 *
 * 특수 니즈라 사이드바에 상시 노출하지 않는다(쓰는 샵에만 메뉴가 생김).
 * 대신 이 니즈를 실제로 느끼는 자리 — 구매 이벤트 숫자를 보는 화면 — 에
 * 안내 카드를 두어, 해당되는 고객만 자연스럽게 들어오게 한다.
 *
 * ⚠️ 지금은 **파일럿 단계**라 그룹이 이미 있는 샵에만 노출한다(= 우리가 태그를
 * 심어준 샵). 그래서 다른 고객에게는 이 기능의 존재 자체가 보이지 않는다.
 * 태그 데이터가 곧 스위치라, 샵을 추가로 열 때 배포 없이 태그만 심으면 된다.
 * 전체 공개할 때는 아래 has_measurement_groups 게이트만 제거하면 된다.
 */
function ProductGroupEntryCard() {
  const { shop } = useShop()

  if (!shop?.has_measurement_groups) {
    return null
  }

  return (
    <Card>
      <CardContent className="flex flex-col sm:flex-row sm:items-center gap-3 py-4">
        <PackageX className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1 text-sm">
          <p className="font-medium text-on-surface">
            공구·협업 상품 매출이 광고 성과에 섞이나요?
          </p>
          <p className="text-on-surface-variant mt-0.5">
            광고와 무관하게 팔린 상품을 그룹으로 묶어두면, 메타에서 그 그룹만 뺀
            성과를 따로 볼 수 있어요.
          </p>
        </div>
        <Link to="/product-groups">
          <Button variant="outline" size="sm" className="shrink-0">
            설정하기
          </Button>
        </Link>
      </CardContent>
    </Card>
  )
}

// ============================================
// 이벤트 통계
// ============================================

function CapiStatsSection({
  onDateClick,
  selectedDate,
  eventFilter,
  onEventFilterChange,
}: {
  onDateClick: (date: string) => void
  selectedDate: string | null
  eventFilter: string
  onEventFilterChange: (value: string) => void
}) {
  const def = getDefaultRange()
  const [startDate, setStartDate] = useState(def.start)
  const [endDate, setEndDate] = useState(def.end)

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const canGoNext = endDate < today

  const {
    data: statsRaw,
    isLoading,
    isError,
  } = useGetMyCapiEventStats({
    start_date: fmtDate(startDate),
    end_date: fmtDate(endDate),
  })

  const handlePrev = () => {
    const s = shiftRange(startDate, endDate, -RANGE_DAYS)
    setStartDate(s.start)
    setEndDate(s.end)
  }
  const handleNext = () => {
    if (!canGoNext) return
    const s = shiftRange(startDate, endDate, RANGE_DAYS)
    if (s.end > today) {
      s.end = new Date(today)
      s.start = new Date(today)
      s.start.setDate(s.start.getDate() - 6)
    }
    setStartDate(s.start)
    setEndDate(s.end)
  }

  // 기본 이벤트 전체 + 데이터에만 있는 추가 이벤트로 chip 생성
  const eventChips = useMemo(() => {
    const stats =
      (statsRaw as { stats: MetaEventStatItem[] } | undefined)?.stats ?? []
    const seen = new Set(stats.map((s) => s.event_name))
    // 기본 순서 전체 포함 + 데이터에는 있지만 기본 목록에 없는 이벤트 추가
    const ordered = [...BASE_EVENT_ORDER]
    for (const name of seen) {
      if (!ordered.includes(name)) ordered.push(name)
    }
    return [
      { value: '', label: '전체' },
      ...ordered.map((e) => ({ value: e, label: eventKo(e) })),
    ]
  }, [statsRaw])

  const { rows, grandTotal, maxTotal } = useMemo(() => {
    const stats =
      (statsRaw as { stats: MetaEventStatItem[] } | undefined)?.stats ?? []
    const dateMap = new Map<string, number>()
    for (const item of stats) {
      if (eventFilter && item.event_name !== eventFilter) continue
      dateMap.set(item.date, (dateMap.get(item.date) ?? 0) + item.total_count)
    }
    const allDates = getDateRange(startDate, endDate)
    const filled = allDates.map((date) => ({
      date,
      total: dateMap.get(date) ?? 0,
    }))
    const total = filled.reduce((a, r) => a + r.total, 0)
    const max = filled.reduce((m, r) => Math.max(m, r.total), 0)
    return { rows: filled, grandTotal: total, maxTotal: max }
  }, [statsRaw, eventFilter, startDate, endDate])

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>이벤트 통계</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium text-on-surface min-w-[140px] text-center">
              {fmtShort(fmtDate(startDate))} ~ {fmtShort(fmtDate(endDate))}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNext}
              disabled={!canGoNext}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* chip 필터 */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {eventChips.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => onEventFilterChange(c.value)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                eventFilter === c.value
                  ? 'bg-primary text-white'
                  : 'bg-surface-low text-on-surface-variant hover:bg-surface-container'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-outline-variant" />
          </div>
        )}
        {isError && (
          <div className="text-center py-12 text-sm text-error">
            통계를 불러오지 못했어요.
          </div>
        )}

        {!isLoading && !isError && (
          <div className="space-y-1">
            {rows.map((row) => {
              const isSelected = selectedDate === row.date
              return (
                <button
                  key={row.date}
                  type="button"
                  onClick={() => row.total > 0 && onDateClick(row.date)}
                  className={`w-full flex items-center gap-3 text-sm py-1 rounded transition-colors ${
                    isSelected
                      ? 'bg-primary/10'
                      : row.total > 0
                        ? 'hover:bg-surface-low cursor-pointer'
                        : 'cursor-default'
                  }`}
                >
                  <span
                    className={`w-12 text-right tabular-nums shrink-0 ${isSelected ? 'font-semibold text-primary' : 'text-on-surface-variant'}`}
                  >
                    {fmtShort(row.date)}
                  </span>
                  <div className="flex-1 h-5 bg-surface-low rounded overflow-hidden relative">
                    {row.total > 0 ? (
                      <div
                        className={`h-full rounded transition-all duration-300 flex items-center pl-2 ${isSelected ? 'bg-primary/80' : 'bg-primary/50'}`}
                        style={{
                          width: `${Math.max((row.total / maxTotal) * 100, 10)}%`,
                        }}
                      >
                        <span className="text-[10px] font-medium text-white drop-shadow-sm">
                          {row.total}
                        </span>
                      </div>
                    ) : (
                      <span className="absolute inset-0 flex items-center pl-2 text-[10px] text-outline-variant">
                        0
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
            <div className="flex items-center gap-3 text-sm pt-1.5 border-t border-border mt-1.5">
              <span className="w-12 text-right font-semibold text-on-surface shrink-0">
                합계
              </span>
              <span className="font-semibold text-on-surface">
                {grandTotal.toLocaleString()}건
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// 이벤트 로그
// ============================================

function CapiLogsSection({
  dateFilter,
  onClearDate,
  eventFilter,
}: {
  dateFilter: string | null
  onClearDate: () => void
  eventFilter: string
}) {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [eventName, setEventName] = useState('')
  const [status, setStatus] = useState('')
  const [detailId, setDetailId] = useState<string | null>(null)

  // 통계 chip 이벤트 필터 → 로그 이벤트 필터 동기화
  useEffect(() => {
    setEventName(eventFilter)
    setPage(1)
  }, [eventFilter])

  // dateFilter 변경 시 페이지 리셋
  useEffect(() => {
    setPage(1)
  }, [dateFilter])

  const {
    data: logsRaw,
    isLoading,
    isError,
    isFetching,
  } = useGetMyCapiEventLogs({
    page,
    limit: PAGE_SIZE,
    event_name: eventName || undefined,
    status: status || undefined,
    date: dateFilter || undefined,
  })

  const logsData = logsRaw as MetaEventLogsListResponse | undefined
  const events = logsData?.events ?? []
  const total = logsData?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: getGetMyCapiEventLogsQueryKey() })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle>이벤트 로그</CardTitle>
            {!isLoading && (
              <span className="text-xs text-on-surface-variant">
                ({total.toLocaleString()}건)
              </span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw
              className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`}
            />
            새로고침
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* 필터 바 */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {/* 날짜 필터 뱃지 */}
          {dateFilter && (
            <button
              type="button"
              onClick={onClearDate}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors"
            >
              {fmtShort(dateFilter)}
              <X className="h-3 w-3" />
            </button>
          )}
          <Select
            variant="sm"
            value={eventName}
            onChange={(e) => {
              setEventName(e.target.value)
              setPage(1)
            }}
            className="w-36"
          >
            {[
              { value: '', label: '전체 이벤트' },
              ...BASE_EVENT_ORDER.map((e) => ({ value: e, label: eventKo(e) })),
            ].map((o) => (
              <SelectOption key={o.value} value={o.value}>
                {o.label}
              </SelectOption>
            ))}
          </Select>
          <Select
            variant="sm"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
            className="w-28"
          >
            {STATUS_OPTIONS.map((o) => (
              <SelectOption key={o.value} value={o.value}>
                {o.label}
              </SelectOption>
            ))}
          </Select>
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-outline-variant" />
          </div>
        )}
        {isError && (
          <div className="text-center py-12 text-sm text-error">
            로그를 불러오지 못했어요.
          </div>
        )}
        {!isLoading && !isError && events.length === 0 && (
          <EmptyState
            icon={<FileText className="h-12 w-12" />}
            title="이벤트 로그가 없어요"
            description="조건에 맞는 이벤트가 없습니다."
          />
        )}

        {/* 2컬럼: 로그 리스트 + 상세 패널 */}
        {/* 모바일: 세로 스택 / 데스크톱: 2컬럼 분할 */}
        {!isLoading && !isError && events.length > 0 && (
          <div className="flex flex-col md:flex-row gap-4">
            {/* 로그 리스트 — 모바일에서 상세 열려있으면 숨김 */}
            <div
              className={`${detailId ? 'hidden md:block md:w-1/2' : 'w-full'} transition-all`}
            >
              <div className="border border-border rounded-lg overflow-hidden divide-y divide-border">
                {events.map((ev: MetaEventLogResponse) => {
                  const failed = ev.status === 'failed'
                  // 전송 시도조차 안 한 건 — 초록 점으로 보이면 "성공했는데 메타에 없다"는
                  // 오해를 만든다. 사유(error_message)까지 같이 노출한다.
                  const skipped = ev.status === 'skipped'
                  const selected = detailId === ev.id
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => setDetailId(selected ? null : ev.id)}
                      // 배지가 최대 3개까지 붙을 수 있어 한 줄에 안 들어갈 수 있다.
                      // 좁아지는 건 모바일만이 아니다 — 상세를 열면 이 목록이 md:w-1/2 로
                      // 반토막 나고, 래퍼가 overflow-hidden 이라 넘치면 스크롤도 없이 잘린다.
                      // 그래서 브레이크포인트로 가르지 않고 항상 줄바꿈을 허용한다.
                      // (넘치지 않으면 애초에 안 감기므로 넓은 화면은 지금과 똑같다.)
                      className={`w-full text-left px-3 sm:px-4 py-3 flex flex-wrap items-center gap-2 sm:gap-3 text-sm transition-colors ${
                        selected
                          ? 'bg-primary/10'
                          : failed
                            ? 'bg-error/5 hover:bg-error/10'
                            : 'hover:bg-surface-low/50'
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          failed
                            ? 'bg-red-500'
                            : skipped
                              ? 'bg-on-surface-variant'
                              : 'bg-green-500'
                        }`}
                      />
                      <span
                        className={`font-medium ${
                          failed
                            ? 'text-error'
                            : skipped
                              ? 'text-on-surface-variant'
                              : 'text-on-surface'
                        }`}
                      >
                        {eventKo(ev.event_name)}
                      </span>
                      {/* 전송 이력 배지 — 보강 재전송 / 즉시 재시도 / 자동 복구를 구분한다.
                          특히 '자동 복구'는 status 가 success 라 배지가 없으면 안 보인다. */}
                      {deliveryBadges(ev).map((b) => (
                        <Badge
                          key={b.key}
                          variant={b.variant}
                          className="whitespace-nowrap text-[10px]"
                        >
                          {b.label}
                        </Badge>
                      ))}
                      {/* fbc 존재 여부 — 광고 클릭 추적 */}
                      {ev.has_fbc && (
                        <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                          fbc
                        </span>
                      )}
                      {failed && ev.error_message && (
                        <span className="text-xs text-error truncate flex-1 ml-1 hidden sm:inline">
                          {ev.error_message}
                        </span>
                      )}
                      {skipped && (
                        <span className="text-xs text-on-surface-variant truncate flex-1 ml-1 hidden sm:inline">
                          전송 안 됨
                          {ev.error_message ? ` — ${ev.error_message}` : ''}
                        </span>
                      )}
                      {/* 사유 스팬이 hidden sm:inline 이라, 모바일에서는 스페이서가
                          없어져 시각이 이벤트명 옆에 붙는다. sm 미만에서만 보충한다. */}
                      {(failed || skipped) && (
                        <span className="flex-1 sm:hidden" />
                      )}
                      {!failed && !skipped && <span className="flex-1" />}
                      <span className="tabular-nums text-on-surface-variant shrink-0 text-xs sm:text-sm">
                        {dateFilter
                          ? fmtTime(ev.created_at)
                          : fmtDateTime(ev.created_at)}
                      </span>
                    </button>
                  )
                })}
              </div>
              <div className="flex justify-center mt-3">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            </div>

            {/* 상세 패널 — 모바일: 전체 너비 + 뒤로가기, 데스크톱: 오른쪽 50% */}
            {detailId && (
              <div className="w-full md:w-1/2 border border-border rounded-lg p-4 bg-surface-lowest md:sticky md:top-4 md:self-start max-h-[80vh] overflow-y-auto">
                {/* 모바일: 목록으로 돌아가기 버튼 */}
                <button
                  onClick={() => setDetailId(null)}
                  className="md:hidden flex items-center gap-1 text-sm text-primary mb-3"
                >
                  <ChevronLeft className="h-4 w-4" />
                  목록으로
                </button>
                <EventDetailPanel
                  eventId={detailId}
                  onClose={() => setDetailId(null)}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// 이벤트 상세 패널
// ============================================

function EventDetailPanel({
  eventId,
  onClose,
}: { eventId: string; onClose: () => void }) {
  const { data: detailRaw, isLoading } = useGetMyCapiEventDetail(eventId)
  const d = detailRaw as MetaEventLogDetailResponse | undefined

  // 리스트 행과 같은 3분법이어야 한다. 이분법이면 스킵 건을 열었을 때 초록 "성공"
  // 뱃지가 뜨고 사유도 안 보여서, 리스트에서 회색으로 구분한 의미가 사라진다.
  // (리스트의 사유 스팬은 hidden sm:inline 이라 모바일에서는 상세가 유일한 단서다.)
  const dFailed = (d?.status as string) === 'failed'
  const dSkipped = (d?.status as string) === 'skipped'

  // 아래 '에러' 블록이 실제로 error_message 를 보여주는가. 전송 이력의 '재전송 오류'가
  // 같은 문자열을 두 번 찍지 않도록 이 값으로 판정한다 (조건은 그 블록과 동일해야 한다).
  const errorShownAbove = (dFailed || dSkipped) && !!d?.error_message

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-on-surface">이벤트 상세</h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-on-surface-variant hover:text-on-surface rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-outline-variant" />
        </div>
      )}

      {!isLoading && d && (
        <div className="space-y-3 text-sm">
          <Row
            label="이벤트"
            value={
              <span className="flex flex-wrap items-center gap-2">
                {eventKo(d.event_name as string)}
                <Badge
                  variant={
                    dFailed ? 'danger' : dSkipped ? 'secondary' : 'success'
                  }
                  className="whitespace-nowrap"
                >
                  {dFailed ? '실패' : dSkipped ? '전송 안 됨' : '성공'}
                </Badge>
                {/* 상태 배지 옆에 전송 이력을 붙인다. '성공' 하나만 있으면
                    첫 시도부터 성공한 건과 자동 복구된 건이 똑같아 보인다. */}
                {deliveryBadges(d).map((b) => (
                  <Badge
                    key={b.key}
                    variant={b.variant}
                    className="whitespace-nowrap"
                  >
                    {b.label}
                  </Badge>
                ))}
              </span>
            }
          />
          <Row label="이벤트 시각" value={fmtFull(d.event_time as string)} />
          <Row label="기록 시각" value={fmtFull(d.created_at as string)} />
          {d.event_source_url && (
            <Row
              label="페이지"
              value={
                <span className="text-xs break-all text-on-surface-variant">
                  {d.event_source_url as string}
                </span>
              }
            />
          )}
          {d.event_id && (
            <Row
              label="이벤트 ID"
              value={
                <code className="text-[11px] bg-surface-low px-1.5 py-0.5 rounded">
                  {d.event_id as string}
                </code>
              }
            />
          )}

          {(dFailed || dSkipped) && d.error_message && (
            <div
              className={`p-2.5 rounded-lg ${
                dSkipped ? 'bg-surface-low' : 'bg-error/10'
              }`}
            >
              <p
                className={`text-xs font-medium mb-0.5 ${
                  dSkipped ? 'text-on-surface-variant' : 'text-error'
                }`}
              >
                {dSkipped ? '전송 안 된 이유' : '에러'}
              </p>
              <p
                className={`text-xs ${
                  dSkipped ? 'text-on-surface-variant' : 'text-error'
                }`}
              >
                {d.error_message as string}
              </p>
            </div>
          )}

          {/* 전송 이력 — 세 경로를 각각 한 줄씩. 배치 재전송은 기존 행을 업데이트하므로
              batch_retry_* 를 안 보면 복구 사실 자체가 화면에서 사라진다. */}
          {/* first_attempt_error 도 진입 조건에 넣는다. 지금은 배치 성공 경로만 이 값을
              쓰고 그 경로는 batch_retry_count 도 올리므로 없어도 되지만, 동기 재시도가
              되살아나 이 필드만 채우면 사유가 조용히 사라진다 — 이 변경이 막으려는 바로
              그 유실이다. */}
          {(d.is_resend ||
            (d.retry_count ?? 0) > 0 ||
            (d.batch_retry_count ?? 0) > 0 ||
            d.batch_retry_status ||
            d.first_attempt_error) && (
            <div className="p-2.5 bg-surface-low rounded-lg text-xs text-on-surface-variant space-y-1.5">
              <p className="font-medium text-on-surface">전송 이력</p>

              {/* 1. PII 보강 재전송 — 이 행 자체가 다시 보낸 건 */}
              {d.is_resend && <p>고객 정보를 보강해 다시 보낸 이벤트예요.</p>}

              {/* 2. 동기 즉시 재시도 — 전송 요청 안에서 곧바로 다시 시도 */}
              {(d.retry_count ?? 0) > 0 && (
                <p>전송 중 즉시 재시도 {d.retry_count}회</p>
              )}

              {/* 3. 배치 재전송 — 스케줄러가 나중에 다시 보낸 것 */}
              {((d.batch_retry_count ?? 0) > 0 || d.batch_retry_status) && (
                <div className="space-y-0.5">
                  <p>
                    자동 재전송 {d.batch_retry_count ?? 0}회
                    {d.batch_retry_status &&
                      ` — ${
                        BATCH_RETRY_STATUS_KO[d.batch_retry_status] ??
                        d.batch_retry_status
                      }`}
                  </p>
                  {d.batch_retry_at && (
                    <p>마지막 시도: {fmtFull(d.batch_retry_at as string)}</p>
                  )}
                  {/* 사유는 원문 그대로 낸다. 성공한 건만 가린다 — 볼 이유가 없다.
                      건너뛴 건도 여기로 온다: 사유가 둘이라 한쪽 문구를 박으면 다른
                      쪽에 거짓말이 되고, 옮기지 못한 사유는 삼켜진다. 영어라도
                      화면에 남기는 쪽을 택했다 (위 BATCH_RETRY_STATUS_KO 주석 참고).

                      단 **위 '에러' 블록이 이미 같은 값을 보여줬으면 띄우지 않는다.**
                      배치가 실패하면 백엔드가 error_message 를 그대로 두고
                      batch_retry_error 에도 같은 문자열을 넣어서, 한 패널에 같은 영어
                      원문이 두 번 찍힌다. prod 실측(2026-08-13, 30일):
                      batch_retry_status='failed' 8건이 전부 이 경우였다
                      (http=400, code=100, subcode=2804004 …).

                      "값이 같으면" 이 아니라 "**위에서 실제로 보여줬으면**" 으로 판정한다.
                      그 블록은 failed·skipped 일 때만 뜨므로, 값만 비교하면 블록이 안 뜬
                      상태에서 사유를 통째로 삼킬 수 있다. */}
                  {d.batch_retry_status !== 'success' &&
                    d.batch_retry_error &&
                    !(
                      errorShownAbove && d.batch_retry_error === d.error_message
                    ) && (
                      <p className="text-error">
                        재전송 오류: {d.batch_retry_error as string}
                      </p>
                    )}
                  {/* 복구된 건은 결론을 말해준다 — 배지만으로는 "그래서 메타에
                      들어갔나?"가 안 풀린다. */}
                  {d.batch_retry_status === 'success' && (
                    <p className="text-on-surface">
                      첫 전송은 실패했지만 자동 재전송으로 메타에 정상
                      반영됐어요.
                    </p>
                  )}
                </div>
              )}

              {/* 첫 시도 실패 사유 — 즉시 재시도와 자동 복구가 함께 쓰는 필드다.
                  배치 복구 시 error_message 가 이쪽으로 옮겨온다. */}
              {d.first_attempt_error && (
                <p>첫 시도 실패 사유: {d.first_attempt_error as string}</p>
              )}
            </div>
          )}

          {d.custom_data && Object.keys(d.custom_data as object).length > 0 && (
            <div className="p-2.5 bg-surface-low rounded-lg">
              <p className="text-xs font-medium text-on-surface mb-1">
                전환 데이터
              </p>
              <pre className="text-[11px] text-on-surface-variant overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(d.custom_data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-on-surface-variant mb-0.5">{label}</dt>
      <dd className="text-on-surface">{value}</dd>
    </div>
  )
}
